"use client"

import type { ReactNode } from "react"

export type SectionCardProps = {
  title?: string
  description?: string
  rightSlot?: ReactNode
  children: ReactNode
  padded?: boolean
  className?: string
  /** Ancla de scroll (búsqueda del Config Hub navega a href#id). */
  id?: string
}

export function SectionCard({
  title,
  description,
  rightSlot,
  children,
  padded = true,
  className = "",
  id,
}: SectionCardProps) {
  return (
    <div
      id={id}
      className={[
        "bg-[var(--redy-surface)] border border-[var(--redy-border)] rounded-[16px]",
        "[box-shadow:var(--shadow-card)]",
        padded ? "p-5 sm:p-6" : "",
        // scroll-mt: que el salto por ancla no esconda la card bajo los
        // headers sticky del admin (breadcrumb + tabs).
        id ? "scroll-mt-24" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {(title || description || rightSlot) && (
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            {title && (
              <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--redy-muted)] mb-1">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-[13px] text-[var(--redy-secondary)]">
                {description}
              </p>
            )}
          </div>
          {rightSlot && <div className="shrink-0">{rightSlot}</div>}
        </div>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  )
}

/**
 * SectionHeader — un header de sección sin la card.
 * Útil para agrupar contenido dentro de PageBody sin envolver en card.
 */
export type SectionHeaderProps = {
  title: string
  count?: number
  description?: string
  rightSlot?: ReactNode
  className?: string
}

export function SectionHeader({
  title,
  count,
  description,
  rightSlot,
  className = "",
}: SectionHeaderProps) {
  return (
    <div className={`flex items-end justify-between gap-4 mb-4 ${className}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="text-[20px] font-bold text-[var(--redy-charcoal)] tracking-[-0.01em]">
            {title}
          </h2>
          {count != null && (
            <span className="text-[12px] font-semibold text-[var(--redy-muted)] tabular-nums">
              {count}
            </span>
          )}
        </div>
        {description && (
          <p className="text-[13px] text-[var(--redy-secondary)] mt-1">
            {description}
          </p>
        )}
      </div>
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </div>
  )
}
